import { type JSX, useEffect, useState } from 'react';

import { anlegen } from '../api/angebote';
import { zeile as kundenzeile, type Kundenzeile } from '../api/kunden';
import { Kundenfeld, Kundensuchfenster } from '../suche/Kundenwahl';
import { ApiFehler } from '../api/fehler';
import { Abschnitt, Feldraster } from '../komponenten/Akte';
import { Erfassung } from '../komponenten/Erfassung';
import { Meldung } from '../komponenten/Meldung';

/** Der heutige Tag als ISO-Datum, ohne Zeitzonensprung. */
function heute(): string {
  const j = new Date();
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

/** Ein Tag in der Zukunft als ISO-Datum. */
function inTagen(tage: number): string {
  const j = new Date();
  j.setDate(j.getDate() + tage);
  return `${j.getFullYear()}-${String(j.getMonth() + 1).padStart(2, '0')}-${String(j.getDate()).padStart(2, '0')}`;
}

/**
 * Die Neuanlage eines Angebots (ANG-01, ANG-07).
 *
 * DREI FELDER UND KEINES MEHR: Kunde, Betreff, Gültigkeitsdatum. Alles
 * Weitere — Texte, Referenz, Ansprechpartner — steht in der Akte und ist
 * dort besser aufgehoben: Ein Formular, das vor der ersten Position zehn
 * Felder verlangt, wird nicht ausgefuellt, sondern umgangen.
 *
 * DIE ANGEBOTSNUMMER IST KEIN EINGABEFELD. Sie entsteht beim Speichern aus
 * dem Nummernkreis (ANG-07, DAT-18) und ist danach unveraenderlich; ein Feld
 * dafuer waere die Einladung, sie von Hand zu vergeben.
 *
 * DIE VORGABE FUER DAS GUELTIGKEITSDATUM steht HIER und nicht im Schema des
 * Servers: Dreissig Tage sind eine Gepflogenheit des Hauses und keine
 * Festlegung des Lastenhefts. Wer sie aendert, aendert eine Maske und keine
 * Rechenregel.
 */
export function AngebotAnlegen({
  anfangsKunde,
  beiAngelegt,
  beiAbbruch,
}: {
  readonly anfangsKunde?: string;
  readonly beiAngelegt: (id: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  /**
   * M-07: Der Kunde wird ueber das SUCHFENSTER gewaehlt, nicht ueber eine
   * eigene Auswahlliste an diesem Feld. "Es gibt keine zweite Art, einen
   * Datensatz zu waehlen: keine Auswahlliste, kein eigener Dialog je Feld."
   *
   * Die frueher hier stehende Trefferliste war genau diese zweite Art — mit
   * eigenem Takt, eigener Zeichengrenze und eigener Darstellung. Feld und
   * Fenster teilt sich die Maske mit den anderen Anlegen-Masken (Kundenwahl).
   */
  const [waehlt, setzeWaehlt] = useState(false);
  const [kunde, setzeKunde] = useState<Kundenzeile | undefined>(undefined);
  const [betreff, setzeBetreff] = useState('');
  const [gueltigBis, setzeGueltigBis] = useState(inTagen(30));
  const [laeuft, setzeLaeuft] = useState(false);
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);

  // Ein aus der Kundenakte heraus angelegtes Angebot bringt den Kunden mit.
  useEffect(() => {
    if (anfangsKunde === undefined || anfangsKunde === '') return;
    void kundenzeile(anfangsKunde)
      .then(setzeKunde)
      .catch(() => undefined);
  }, [anfangsKunde]);

  async function absenden(): Promise<void> {
    if (kunde === undefined || betreff === '') return;
    setzeLaeuft(true);
    try {
      const neu = await anlegen({
        kunde: kunde.id,
        betreff,
        belegdatum: heute(),
        gueltigBis,
      });
      beiAngelegt(neu.id);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    } finally {
      setzeLaeuft(false);
    }
  }

  return (
    <div className="maske maske--weit erfassung">
      {fehler !== undefined && <Meldung fehler={fehler} />}

      {waehlt && (
        <Kundensuchfenster
          beiWahl={setzeKunde}
          beiFehler={setzeFehler}
          beiSchliessen={() => setzeWaehlt(false)}
        />
      )}

      {/*
        ÄA-41, Seitentyp Erfassung: „Neues Angebot" steht im Seitenkopf, die
        Karte trägt nur noch den Abschnitt mit den drei Feldern. „Anlegen"
        steht oben rechts und am Ende. Das Suchfenster steht außerhalb der
        Karte: Es ist ein eigener Dialog und gehört nicht in dieses Formular.
      */}
      <Erfassung
        beschriftung="Neues Angebot"
        beiAbsenden={() => void absenden()}
        absenden={{
          text: laeuft ? 'Wird gespeichert …' : 'Anlegen',
          gesperrt: kunde === undefined || betreff === '' || laeuft,
        }}
        abbrechen={{ text: 'Abbrechen', beiKlick: beiAbbruch }}
      >
        {/* ANG-01; ANG-07: Die Nummer ist kein Eingabefeld. */}
        <Abschnitt titel="Angebot" zusatz="Nummer wird beim Speichern vergeben">
          <Feldraster>
            <Kundenfeld
              kennung="anlegen-kunde"
              kunde={kunde}
              offen={waehlt}
              beiSuchen={() => setzeWaehlt(true)}
            >
              {/* PRD-12 */}
              <p className="feld__hilfe">
                Die Preisführung wird nach der Kundenart vorbelegt: Privatkunden brutto,
                Geschäftskunden netto.
              </p>
            </Kundenfeld>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="anlegen-betreff">
                Betreff
              </label>
              <input
                id="anlegen-betreff"
                type="text"
                value={betreff}
                onChange={(e) => setzeBetreff(e.target.value)}
                placeholder="Fahrsicherheitstraining für Ihre Außendienstmitarbeiter"
              />
            </div>

            <div className="feld">
              <label className="feld__beschriftung" htmlFor="anlegen-gueltig">
                Gültig bis
              </label>
              <input
                id="anlegen-gueltig"
                type="date"
                value={gueltigBis}
                min={heute()}
                onChange={(e) => setzeGueltigBis(e.target.value)}
              />
              {/* ANG-17 */}
              <p className="feld__hilfe">
                Nach diesem Tag wechselt das Angebot selbsttätig auf „abgelaufen“.
              </p>
            </div>
          </Feldraster>
        </Abschnitt>
      </Erfassung>
    </div>
  );
}
